import { useEffect, useState } from "react";
import { useGoogleOneTapLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// Re-use the same overlay exported from GoogleSignInButton to keep one source
// of truth for the animation. We import the raw component via a lazy approach
// to avoid a circular dep (GoogleSignInButton doesn't import OneTap).
import GoogleSignInButton from "@/components/GoogleSignInButton";

// We need only the overlay — trick: render a hidden button and grab its portal.
// Simpler: just duplicate the tiny overlay inline here.
const LOGO     = "/assets/images/kiddo-logo.png";
const WORDMARK = "/assets/images/kiddo-wordmark.png";

const GoogleLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
  </svg>
);

function GoogleSpinRing({ size = 88 }) {
  const r = size / 2;
  const c = Math.PI * size;
  return (
    <svg width={size + 16} height={size + 16} viewBox={`0 0 ${size+16} ${size+16}`}
      style={{ animation: "gc-spin 1.4s linear infinite" }} aria-hidden="true">
      {[["#FFC107",0],["#FF3D00",1],["#4CAF50",2],["#1976D2",3]].map(([color, i]) => (
        <circle key={i} cx={r+8} cy={r+8} r={r} fill="none" strokeWidth="3.5" stroke={color}
          strokeDasharray={`${c/4} ${c*3/4}`} strokeDashoffset={`-${c/4*i}`} strokeLinecap="round" />
      ))}
    </svg>
  );
}

function ConnectingOverlay() {
  return (
    <>
      <style>{`
        @keyframes gc-fade-in  { from{opacity:0} to{opacity:1} }
        @keyframes gc-slide-up { from{opacity:0;transform:translateY(28px) scale(0.97)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes gc-spin     { to{transform:rotate(360deg)} }
        @keyframes gc-pulse    { 0%,100%{opacity:1} 50%{opacity:.4} }
        @keyframes gc-dot-flow { 0%{transform:translateX(0);opacity:0} 20%{opacity:1} 80%{opacity:1} 100%{transform:translateX(52px);opacity:0} }
        @keyframes gc-progress { 0%{transform:translateX(-100%) scaleX(.4)} 50%{transform:translateX(80%) scaleX(1)} 100%{transform:translateX(200%) scaleX(.4)} }
        .gc-ot-overlay { animation:gc-fade-in .22s ease both }
        .gc-ot-card    { animation:gc-slide-up .38s cubic-bezier(.22,1,.36,1) .06s both }
        .gc-ot-ptxt    { animation:gc-pulse 2s ease-in-out infinite }
        .gc-ot-d1 { animation:gc-dot-flow 1.4s ease-in-out infinite }
        .gc-ot-d2 { animation:gc-dot-flow 1.4s ease-in-out .47s infinite }
        .gc-ot-d3 { animation:gc-dot-flow 1.4s ease-in-out .94s infinite }

        /* ── dark-mode overrides ── */
        .gc-ot-bg          { background: #EBF5F2 }
        .gc-ot-card-bg     { background: #ffffff }
        .gc-ot-header-band { background: linear-gradient(135deg, #EBF5F2 0%, #d4ede8 100%) }
        .gc-ot-logo-circle { background: #ffffff }
        .gc-ot-icon-circle { background: #ffffff; border-color: #f3f4f6 }
        .gc-ot-heading     { color: #1A2E2A }
        .gc-ot-subtext     { color: #4A5568 }
        .gc-ot-prog-track  { background: #C2E0DA }

        .dark .gc-ot-bg          { background: #091714 }
        .dark .gc-ot-card-bg     { background: #161C24 }
        .dark .gc-ot-header-band { background: linear-gradient(135deg, #0d2420 0%, #0a1c18 100%) }
        .dark .gc-ot-logo-circle { background: #1E252E }
        .dark .gc-ot-icon-circle { background: #1E252E; border-color: #2A323C }
        .dark .gc-ot-heading     { color: #DFF2EE }
        .dark .gc-ot-subtext     { color: #94A3B8 }
        .dark .gc-ot-prog-track  { background: rgba(20,184,166,0.15) }
      `}</style>
      <div className="gc-ot-overlay gc-ot-bg fixed inset-0 z-[400] flex items-center justify-center px-4"
        data-testid="google-connecting-overlay">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{ backgroundImage:"radial-gradient(circle,#2A7B6F 1px,transparent 1px)", backgroundSize:"28px 28px" }} />
        <div className="gc-ot-card gc-ot-card-bg relative w-full max-w-sm rounded-3xl overflow-hidden shadow-[0_24px_80px_rgba(0,0,0,0.12)] dark:shadow-[0_24px_80px_rgba(0,0,0,0.5)]">
          <div className="gc-ot-header-band relative pt-10 pb-12 flex items-center justify-center overflow-hidden">
            <div className="absolute"><GoogleSpinRing size={88} /></div>
            <div className="gc-ot-logo-circle relative z-10 w-[88px] h-[88px] rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.1)] flex items-center justify-center">
              <GoogleLogo />
            </div>
          </div>
          <div className="gc-ot-card-bg -mt-6 rounded-t-[2rem] pt-5 pb-8 px-6 flex flex-col items-center text-center">
            <div className="flex items-center gap-3 mb-5 mt-1">
              <div className="gc-ot-icon-circle w-9 h-9 rounded-full border shadow-sm flex items-center justify-center shrink-0"><GoogleLogo /></div>
              <div className="relative w-14 h-4 flex items-center overflow-hidden shrink-0">
                {[["#FFC107","gc-ot-d1"],["#4CAF50","gc-ot-d2"],["#1976D2","gc-ot-d3"]].map(([bg,cls]) => (
                  <span key={cls} className={`${cls} absolute left-0 w-2.5 h-2.5 rounded-full`} style={{ background:bg, top:"50%", marginTop:"-5px" }} />
                ))}
              </div>
              <div className="gc-ot-icon-circle w-9 h-9 rounded-full border shadow-sm flex items-center justify-center shrink-0">
                <img src={LOGO} alt="Kiddo+" className="w-6 h-6 rounded-full object-cover" />
              </div>
            </div>
            <h2 className="gc-ot-heading font-kp font-extrabold text-[20px] leading-tight">Connecting your account</h2>
            <p className="gc-ot-subtext gc-ot-ptxt text-[13px] mt-1.5">Signing you in with Google…</p>
            <div className="gc-ot-prog-track mt-5 w-full h-1 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ background:"linear-gradient(90deg,#4CAF50,#1976D2,#FFC107,#FF3D00)", width:"60%", marginLeft:"-10%", animation:"gc-progress 1.8s ease-in-out infinite" }} />
            </div>
            <img src={WORDMARK} alt="Kiddo+" className="mt-6 h-5 w-auto opacity-40 dark:brightness-0 dark:invert dark:opacity-30" />
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Renders nothing — just triggers Google's One Tap prompt in the top-right
 * corner for anonymous visitors. Returning Google-logged-in users can sign
 * in without ever leaving the page. Hides itself once the user logs in or
 * dismisses the prompt.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function GoogleOneTap({ enabled = true }) {
  const { user, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [connecting, setConnecting] = useState(false);

  useGoogleOneTapLogin({
    disabled: !enabled || !!user,
    cancel_on_tap_outside: false,
    auto_select: false,
    onSuccess: async (credentialResponse) => {
      setConnecting(true);
      try {
        const res = await loginWithGoogle({ credential: credentialResponse.credential });
        toast.success(res.is_new ? "Welcome to Kiddo+ 🌿" : "Welcome back!");
        if (res.user?.role === "admin") navigate("/admin");
        else if (res.is_new) navigate("/onboarding");
        else navigate("/activities");
      } catch (err) {
        setConnecting(false);
        toast.error(err?.response?.data?.detail || "Google sign-in failed. Please try again.");
      }
    },
    onError: () => {
      // Silently ignore — user dismissed One Tap or it failed to render
      // (e.g. blocked by browser settings). The full button stays available.
    },
  });

  useEffect(() => {
    return () => {
      try {
        if (window.google?.accounts?.id?.cancel) {
          window.google.accounts.id.cancel();
        }
      } catch (_) {
        /* no-op */
      }
    };
  }, []);

  return connecting ? <ConnectingOverlay /> : null;
}
